import { object, string, number, mixed, ObjectSchema, InferType, boolean } from "yup";
import { MovimentacaoObraFormContract } from "../contracts/gerar-movimentacao-obra.contract";
import { ModalityEnum, MovementOriginEnum, SectorEnum } from "../../../shared/utils/enum/enum";
import { UnitOfMeasureEnum } from "../../../shared/components/formulario/select/SelectUnitOfMeasure";

export enum MovementTypeEnum {
    INCOME = "income",
    OUTCOME = "outcome",
}
export enum MovementCategoryEnum {
    CLIENT_SHARE = 'client_share',
    WORK_INVESTMENT = 'work_investment',
    EMPLOYEE_PAYMENT = 'employee_payment',
    EQUIPMENT_RENTAL = 'equipment_rental',
    TRANSPORT = 'transport',
    SUPPLIES = 'supplies',
    EQUIPMENT_MAINTENANCE = 'equipment_maintenance',
    COMPANY_PAYMENTS = 'company_payments',
    SINGLE = 'single',
    TAX_REFUND = 'tax_refund'
}

export const categorias: Record<string, string> = {
    client_share: "Rateio Cliente",
    work_investment: "Investimento na Obra",
    employee_payment: "Pagamento Funcionário",
    equipment_rental: "Aluguel de Equipamento",
    transport: "Transporte",
    supplies: "Insumos",
    equipment_maintenance: "Manutenção de Equipamento",
    company_payments: "Pagamentos da Empresa",
    single: "Lançamento Avulso",
    tax_refund: "Devolução de Impostos"
};

export const tiposMovimentacao: Record<string, string> = {
    income: "Entrada",
    outcome: "Saída",
};

export const modalidade: Record<string, string> = {
    meter: "Metro Linear",
    square_meter: "m²",
    cubic_meter: "m³",
    kilogram: "Kg",
    ton: "Tonelada",
    bag: "Saco",
    liter: "Litro",
    unit: "Unidade",
    set: "Conjunto",
    package: "Pacote",
    hour: "Hora(s)",
    day: "Dia(s)",
    month: "Mês",
    allowance: "Verba",
    service: "Serviço",
    daily: "Dia(s)",
    square_meters: "m²",
    time: "Hora(s)",
};

export const movimentacaoObraSchema: ObjectSchema<MovimentacaoObraFormContract> = object({
    idSector: string().required("É obrigatório informar o setor da movimentação!"),
    origin: mixed<MovementOriginEnum>().optional(),
    isPagamentoColaborador: boolean().default(false),
    clientId: string().when("origin", {
        is: MovementOriginEnum.WORK,
        then: (schema) => schema.required("É obrigatório informar o cliente!"),
        otherwise: (schema) => schema.notRequired(),
    }),
    workId: string().when("origin", {
        is: MovementOriginEnum.WORK,
        then: (schema) => schema.required("É obrigatório informar a obra!"),
        otherwise: (schema) => schema.notRequired(),
    }),
    employeeId: string().when("isPagamentoColaborador", {
        is: true,
        then: (schema) => schema.required("É obrigatório informar o colaborador!"),
        otherwise: (schema) => schema.notRequired(),
    }),
    movementType: mixed<MovementTypeEnum>()
        .oneOf(Object.values(MovementTypeEnum), "Tipo de movimentação inválido")
        .required("É obrigatório informar o tipo da movimentação!"),
    receiptPdf: string().optional(),
    quantityToPay: number().required("A quantidade trabalhada deve ser informada!"),
    value: number()
        .typeError("Valor total deve ser um número")
        .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
                const cleaned = originalValue.replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/g, "");
                if (!cleaned) return undefined;
                const valorNumber = Number(cleaned);
                return isNaN(valorNumber) ? undefined : valorNumber;
            }
            return value;
        })
        .required("É obrigatório informar o valor da movimentação!")
        .default(0),
    dateMovement: string()
        .required("É obrigatório informar a data da movimentação!"),
    idChartOfAccounts: string().required("É obrigatório informar um plano de contas!"),
    description: string().default("").optional(),
    unit: mixed<UnitOfMeasureEnum>()
        .oneOf(Object.values(UnitOfMeasureEnum), "Unidade inválida")
        .required("É obrigatório informar a unidade da movimentação!"),

    // Novos campos para movimentação bancária
    isMovimentToBank: boolean()
        .required("Informar se a movimentação é para o banco é obrigatório"),
    idBank: string().when("isMovimentToBank", {
        is: true,
        then: (schema) => schema.required("É obrigatório informar o banco"),
        otherwise: (schema) => schema.notRequired(),
    }),
    idAgency: string().when("isMovimentToBank", {
        is: true,
        then: (schema) => schema.required("É obrigatório informar a agência"),
        otherwise: (schema) => schema.notRequired(),
    }),
    idAccount: string().when("isMovimentToBank", {
        is: true,
        then: (schema) => schema.required("É obrigatório informar a conta"),
        otherwise: (schema) => schema.notRequired(),
    }),
    idNfs: string().optional().default(null),

    IdInvoicePurchase: string().optional().default(null),
});

export type MovimentacaoObraFormData = InferType<typeof movimentacaoObraSchema>;